import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Boton, EsqueletoTabla, Pill, TituloPagina, Vacio, cn } from "@iavmx/ui";
import { Chip, NOMBRE_ORIGEN } from "../componentes";
import { leer } from "../api";
import { useSesion } from "../sesion";
import { relativa } from "../fechas";
import type { Aviso } from "../tipos";

export function Avisos() {
  const { api } = useSesion();
  const qc = useQueryClient();
  const [filtro, setFiltro] = React.useState<"todos" | "sin_leer">("todos");
  const q = useQuery({
    queryKey: ["avisos", "todos"],
    queryFn: () => api.api.avisos.$get({ query: {} }).then((r) => leer<{ avisos: Aviso[]; sinLeer: number }>(r)),
  });
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["avisos"] });
    void qc.invalidateQueries({ queryKey: ["inicio"] });
  };
  const leida = useMutation({
    mutationFn: (id: string) => api.api.avisos[":id"].leida.$post({ param: { id } }).then((r) => leer(r)),
    onSuccess: refrescar,
  });
  const todas = useMutation({
    mutationFn: () => api.api.avisos.leidas.$post().then((r) => leer(r)),
    onSuccess: refrescar,
  });
  if (q.isLoading) return <EsqueletoTabla columnas={2} />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const avisos = q.data.avisos.filter((a) => filtro === "todos" || !a.leidaEn);
  return (
    <>
      <TituloPagina
        descripcion={`${q.data.sinLeer} sin leer · ${q.data.avisos.length} en total. También llegan en tu resumen diario por correo.`}
        accion={q.data.sinLeer > 0 ? <Boton variante="contorno" tamano="sm" onClick={() => todas.mutate()} disabled={todas.isPending}>{todas.isPending ? "Marcando…" : "Marcar todos como leídos"}</Boton> : undefined}
      >
        Avisos
      </TituloPagina>
      <div className="mb-4 flex gap-2" role="group" aria-label="Filtrar avisos">
        <Chip activo={filtro === "todos"} onClick={() => setFiltro("todos")} n={q.data.avisos.length}>Todos</Chip>
        <Chip activo={filtro === "sin_leer"} onClick={() => setFiltro("sin_leer")} n={q.data.sinLeer}>Sin leer</Chip>
      </div>
      {avisos.length === 0 ? (
        <Vacio titulo={filtro === "sin_leer" ? "Nada sin leer" : "Sin avisos"} texto="Cuando una campaña se atrase, una propuesta se abra o alguien acepte tu invitación, aparece aquí." />
      ) : (
        <ul className="divide-y divide-linea border-y border-linea">
          {avisos.map((a) => (
            <li key={a.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-start sm:gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Pill tono={a.leidaEn ? "neutro" : "azul"}>{NOMBRE_ORIGEN[a.app] ?? a.app}</Pill>
                  <p className={cn("font-ui text-sm", a.leidaEn ? "text-tinta-3" : "font-semibold")}>{a.titulo}</p>
                </div>
                {a.texto && <p className="mt-1 font-ui text-sm text-tinta-2">{a.texto}</p>}
                <p className="mt-1 font-ui text-xs text-tinta-tenue">{relativa(a.creadoEn)}</p>
              </div>
              <div className="flex shrink-0 gap-2">
                {a.url && <Boton variante="liga" tamano="sm" asChild><a href={a.url}>Abrir</a></Boton>}
                {!a.leidaEn && <Boton variante="contorno" tamano="sm" className="min-h-9" onClick={() => leida.mutate(a.id)} disabled={leida.isPending}>Marcar leído</Boton>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
